import { computed, inject, Injectable } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { SearchResultEntityType } from './models';
import { QuickSearchStore } from './quick-search';
import { QuickSearchResult } from './quick-search.model';
import { SearchResultLayerCheckService } from './search-result-layer-check.service';
import { SearchResultService } from './search-result.service';

interface EntityRequest {
  type: SearchResultEntityType;
  identifiers: string[];
}

@Injectable({ providedIn: 'root' })
export class QuickSearchLayerCheckStateService {
  private readonly store = inject(QuickSearchStore);
  private readonly searchResultService = inject(SearchResultService);
  private readonly layerCheckService = inject(SearchResultLayerCheckService);

  private readonly entityRequests = computed(() => {
    const requestMap = new Map<SearchResultEntityType, Set<string>>();

    // Build set of selected doc_ids from searchResultService
    const selectedDocIds = new Set(
      Object.keys(this.searchResultService.searchResults()),
    );

    // Only process items that are in selectedDocIds
    const selectedResults = this.store
      .items()
      .filter((item) => selectedDocIds.has(item.doc_id!));

    const addRequest = (
      type: SearchResultEntityType,
      identifiers: string[],
    ) => {
      if (!requestMap.has(type)) {
        requestMap.set(type, new Set());
      }
      const current = requestMap.get(type)!;
      identifiers.forEach((id) => {
        if (id) current.add(id);
      });
    };

    for (const result of selectedResults) {
      const request = this.mapResultToEntity(result);
      if (!request) continue;
      addRequest(request.type, request.identifiers);
    }

    return Array.from(requestMap.entries()).map(([type, ids]) => ({
      type,
      identifiers: [...ids],
    }));
  });

  private readonly requiredLayers = toSignal(
    toObservable(this.entityRequests).pipe(
      switchMap((requests) => this.resolveRequiredLayers(requests)),
    ),
    { initialValue: [] as string[] },
  );

  readonly missingLayers = toSignal(
    toObservable(this.requiredLayers).pipe(
      switchMap((requiredLayers) =>
        this.layerCheckService.getMissingLayers(requiredLayers),
      ),
    ),
    { initialValue: [] as string[] },
  );

  private resolveRequiredLayers(
    requests: EntityRequest[],
  ): Observable<string[]> {
    if (!requests.length) {
      return of([]);
    }

    return forkJoin(
      requests.map((request) =>
        this.layerCheckService
          .getRequiredLayerNames(request.type, request.identifiers)
          .pipe(catchError(() => of([]))),
      ),
    ).pipe(map((layers) => [...new Set(layers.flat())]));
  }

  private getNumericIdentifierFromDocId(
    docId: string | null | undefined,
  ): string | null {
    if (!docId) return null;
    // quick-search survey label doc_id can include varying alpha prefixes
    // Identifier only needs the numeric part.
    const match = /\d+$/.exec(docId);
    return match?.[0] ?? null;
  }

  private mapResultToEntity(result: QuickSearchResult): EntityRequest | null {
    switch (result.type) {
      case 'address':
      case 'address_pfi':
        return { type: 'address', identifiers: [] };
      case 'parcel':
        return { type: 'parcel', identifiers: [] };
      case 'property':
        return { type: 'property', identifiers: [] };
      case 'survey_mark':
        return {
          type: 'surveyMark',
          identifiers: result.mark_id ? [String(result.mark_id)] : [],
        };
      case 'survey_label': {
        const identifier = this.getNumericIdentifierFromDocId(result.doc_id);
        return {
          type: 'surveyLabel',
          identifiers: identifier ? [identifier] : [],
        };
      }
      case 'lga':
        return { type: 'lga', identifiers: [] };
      case 'locality':
        return { type: 'locality', identifiers: [] };
      case 'road':
        return { type: 'road', identifiers: [] };
      case 'parish':
        return { type: 'parish', identifiers: [] };
      case 'township':
        return { type: 'township', identifiers: [] };
      case 'place':
        return { type: 'place', identifiers: [] };
      default:
        return null;
    }
  }

  async enableLayers() {
    await this.layerCheckService.enableLayers(this.missingLayers());
  }
}
